import { initializeApp, getApps } from 'firebase/app'
import { getDatabase, ref, set, update, onValue, remove, onDisconnect } from 'firebase/database'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { PresenceProvider, PresenceSession } from './PresenceProvider'
import { PRESENCE_SESSION_ID } from './session'
import common from '@/services/common'

// ---------------------------------------------------------------------------
// Internal: Firebase app bootstrap
// ---------------------------------------------------------------------------

function getFirebase() {
  if (getApps().length === 0) {
    initializeApp({
      apiKey:      process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain:  process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
      projectId:   process.env.VUE_APP_FIREBASE_PROJECT_ID,
      appId:       process.env.VUE_APP_FIREBASE_APP_ID
    })
  }
  return { db: getDatabase(), auth: getAuth() }
}

async function fetchFirebaseToken() {
  const apiBase = process.env.VUE_APP_WEBAPI_URI
  const authToken = common.getAuthToken()
  const res = await fetch(`${apiBase}/presence/token`, {
    headers: { Authorization: `Bearer ${authToken}` }
  })
  if (res.status === 403) return null
  if (!res.ok) throw new Error(`Firebase token fetch failed: ${res.status}`)
  const { token } = await res.json()
  return token
}

// ---------------------------------------------------------------------------
// FirebasePresenceSession
// ---------------------------------------------------------------------------

class FirebasePresenceSession extends PresenceSession {
  constructor(memberRef, sessionId, profile) {
    super()
    this._memberRef = memberRef
    this._sessionId = sessionId
    // Latest known profile state, kept so the member node can be restored
    // verbatim (isEditing, activeTab, ...) after a reconnect re-creates it.
    this._profile = { ...profile }
    this._unsubscribeConnected = null
  }

  get sessionId() { return this._sessionId }

  async update(updates) {
    Object.assign(this._profile, updates)
    return update(this._memberRef, updates)
  }

  async leave() {
    if (this._unsubscribeConnected) {
      this._unsubscribeConnected()
      this._unsubscribeConnected = null
    }
    return remove(this._memberRef)
  }
}

// ---------------------------------------------------------------------------
// FirebasePresenceProvider
// ---------------------------------------------------------------------------

export class FirebasePresenceProvider extends PresenceProvider {
  constructor() {
    super()
    this._db = null
  }

  async enter(quoteId, companyId, profile) {
    const { db, auth } = getFirebase()

    const customToken = await fetchFirebaseToken()
    if (!customToken) return null

    await signInWithCustomToken(auth, customToken)

    await update(ref(db, `quotes/${quoteId}`), { companyId })

    // Reuse the tab-wide presence session ID so the avatar identity matches
    // the X-Presence-Session-Id header sent with this tab's save requests.
    const sessionId = PRESENCE_SESSION_ID
    const memberRef = ref(db, `quotes/${quoteId}/presence/${sessionId}`)

    await set(memberRef, { ...profile, connectedAt: Date.now() })
    await onDisconnect(memberRef).remove()

    const session = new FirebasePresenceSession(memberRef, sessionId, profile)

    // When the connection drops (sleep, network blip) the server runs the
    // onDisconnect removal, and onDisconnect registrations are one-shot — so on
    // every reconnect the member node must be re-created and the removal
    // re-registered, or this tab's avatar stays gone until a page reload.
    let wasConnected = true // enter() just completed writes over a live connection
    session._unsubscribeConnected = onValue(ref(db, '.info/connected'), (snapshot) => {
      const isConnected = snapshot.val() === true
      if (isConnected && !wasConnected) {
        // Re-register the removal BEFORE re-creating the node so an immediate
        // second drop can never strand a ghost member.
        onDisconnect(memberRef).remove()
          .then(() => set(memberRef, { ...session._profile, connectedAt: Date.now() }))
          .catch((e) => console.warn('[FirebasePresence] presence restore after reconnect failed:', e))
      }
      wasConnected = isConnected
    })

    this._db = db
    return session
  }

  subscribeToMembers(quoteId, callback) {
    const presenceRef = ref(this._db, `quotes/${quoteId}/presence`)
    return onValue(presenceRef, (snapshot) => {
      const raw = snapshot.val() || {}
      const valid = []
      for (const [sessionId, data] of Object.entries(raw)) {
        if (!data.userId) {
          remove(ref(this._db, `quotes/${quoteId}/presence/${sessionId}`)).catch(() => {})
          continue
        }
        valid.push({ sessionId, ...data })
      }
      callback(valid)
    })
  }

  subscribeToQuoteSaves(quoteId, callback) {
    const quoteRef = ref(this._db, `quotes/${quoteId}`)
    let seed = null
    return onValue(quoteRef, (snapshot) => {
      const data = snapshot.val()
      if (seed === null) { seed = data?.lastSavedAt ?? 0; return }
      if (data?.lastSavedAt > seed) { seed = data.lastSavedAt; callback(data) }
    })
  }

  subscribeToQuoteEntity(quoteId, callback) {
    const quoteRef = ref(this._db, `quotes/${quoteId}`)
    let seed = null
    return onValue(quoteRef, (snapshot) => {
      const data = snapshot.val()
      if (seed === null) { seed = data?.lastSavedAt ?? 0; return }
      if (data?.lastSavedAt > seed && data?.entity) {
        seed = data.lastSavedAt
        callback({ ...data.entity, savedBy: data.savedBy, savedBySession: data.savedBySession, savedByName: data.savedByName, lastSavedAt: data.lastSavedAt, source: data.source })
      }
    })
  }

  subscribeToQuoteDeleted(quoteId, callback) {
    const quoteRef = ref(this._db, `quotes/${quoteId}`)
    let seed = null
    return onValue(quoteRef, (snapshot) => {
      const data = snapshot.val()
      if (seed === null) { seed = data?.deletedAt ?? 0; return }
      if (data?.deletedAt > seed) {
        seed = data.deletedAt
        callback({
          deletedAt:        data.deletedAt,
          deletedBy:        data.deletedBy,
          deletedBySession: data.deletedBySession,
          deletedByName:    data.deletedByName
        })
      }
    })
  }

  subscribeToCustomerUpdates(customerId, callback) {
    const updateRef = ref(this._db, `customers/${customerId}`)
    let seed = null
    return onValue(
      updateRef,
      (snapshot) => {
        const data = snapshot.val()
        if (seed === null) { seed = data?.updatedAt ?? 0; return }
        if (data?.updatedAt > seed) { seed = data.updatedAt; callback(data) }
      },
      (error) => console.error('[Firebase] customerUpdates error', customerId, error.message)
    )
  }

  subscribeToVehicleUpdates(vehicleId, callback) {
    const updateRef = ref(this._db, `vehicles/${vehicleId}`)
    let seed = null
    return onValue(
      updateRef,
      (snapshot) => {
        const data = snapshot.val()
        if (seed === null) { seed = data?.updatedAt ?? 0; return }
        if (data?.updatedAt > seed) { seed = data.updatedAt; callback(data) }
      },
      (error) => console.error('[Firebase] vehicleUpdates error', vehicleId, error.message)
    )
  }
}
